/**
 * Shape the engine's sweep preview for the screen. Pure: no React, no fetch.
 *
 * The engine answers with counts and skip reasons in its own vocabulary;
 * the operator needs to know two things — how many searches, and why some
 * combinations never became one. Searches are billed, so the total is the
 * headline and the reasons are the explanation, never a footnote.
 */

/** Engine skip reasons → what the operator reads. Unknown reasons pass through. */
export const SKIP_LABELS = {
	office_not_mapped_to_price_seeker:       'offices with no Price Seeker mapping (My Business → Admin → Offices)',
	no_tier_covers_the_duration:             'durations no rate-card tier covers',
	rule_and_strategy_share_no_office:       'strategies whose offices never meet this rule\'s',
	strategy_covers_none_of_the_rule_s_dates:'strategies whose period misses every pickup date',
	strategy_has_no_duration:                'strategies with no duration',
	no_rate_card_face_with_a_site:           'no rate card has a Price Seeker site yet',
	face_site_not_a_price_seeker_id:         'rate cards whose site is not a Price Seeker site',
	discarded_by_launch_path:                'rows the search API dropped (no scraper queue for that site)',
}

export function labelSkip(reason) {
	return SKIP_LABELS[reason] || reason.replace(/_/g, ' ')
}

/**
 * `formatPreview(raw)` → { refused, total, rules: [{ ruleId, reason, pickupDates, searches, offices, skipped: [{label, count}] }] }
 */
export function formatPreview(raw) {
	if (!raw) return { refused: 'no answer from the engine', total: 0, rules: [] }
	if (raw.refused) return { refused: raw.refused, total: 0, rules: [] }
	const rules = (raw.rules || []).map(r => ({
		ruleId: r.rule_id,
		reason: r.reason,
		pickupDates: r.pickup_dates,
		searches: r.searches,
		offices: Object.keys(r.per_office || {}).length,
		skipped: Object.entries(r.skipped || {})
			.filter(([, n]) => n > 0)
			.sort((a, b) => b[1] - a[1])
			.map(([reason, count]) => ({ label: labelSkip(reason), count })),
	}))
	return { refused: null, total: raw.total ?? rules.reduce((s, r) => s + r.searches, 0), rules }
}

/** One sentence for the headline. */
export function headline(fmt, { ruleName } = {}) {
	if (fmt.refused) return `The sweep cannot run: ${fmt.refused}`
	if (fmt.rules.length === 0) return ruleName ? `${ruleName} would launch nothing right now` : 'Nothing is due right now'
	const n = fmt.total
	return ruleName
		? `${ruleName} would launch ${n} search${n === 1 ? '' : 'es'}`
		: `${fmt.rules.length} rule${fmt.rules.length === 1 ? '' : 's'} due now, ${n} search${n === 1 ? '' : 'es'} in total`
}
